import React, { useEffect, useMemo, useState } from 'react';
import { jwtDecode } from 'jwt-decode';
import { useAuth } from '../contexts/AuthContext';
import { PushBellButton } from './PushNotificationManager';
import VehicleUrgentHeaderBadge from './VehicleUrgentHeaderBadge';
import GoogleChatIcon, { openGoogleChat } from './icons/GoogleChatIcon';
import './Header.css';

const Header = ({ leftActions = null }) => {
  const { user, logout } = useAuth();
  const [nowTs, setNowTs] = useState(() => Date.now());

  // ✅ 토큰 만료까지 남은 시간 표시(로그아웃 옆)
  const tokenExpiresAtMs = useMemo(() => {
    // 1) 백엔드 세션 토큰: user.session_expires_at_ms 우선
    const sessExp = (user && typeof user === 'object') ? user.session_expires_at_ms : null;
    const sessExpMs = (sessExp != null && String(sessExp).trim() !== '' && !Number.isNaN(Number(sessExp)))
      ? Number(sessExp)
      : null;
    if (sessExpMs) return sessExpMs;

    // 2) 구글 ID 토큰: exp claim
    const token = (user && typeof user === 'object' && user.token) ? String(user.token) : '';
    if (!token) return null;
    try {
      const decoded = jwtDecode(token);
      const expSec = decoded?.exp;
      if (typeof expSec !== 'number' || !Number.isFinite(expSec)) return null;
      return expSec * 1000;
    } catch (e) {
      return null;
    }
  }, [user]);

  const remainLabel = useMemo(() => {
    if (!tokenExpiresAtMs) return '';
    const diff = tokenExpiresAtMs - nowTs;
    if (!Number.isFinite(diff)) return '';
    if (diff <= 0) return '만료됨';
    const sec = Math.floor(diff / 1000);
    const mm = String(Math.floor(sec / 60)).padStart(2, '0');
    const ss = String(sec % 60).padStart(2, '0');
    return `${mm}:${ss}`;
  }, [nowTs, tokenExpiresAtMs]);

  useEffect(() => {
    // 1초마다 갱신 (표시용)
    if (!tokenExpiresAtMs) return undefined;
    const id = setInterval(() => {
      const now = Date.now();
      setNowTs(now);
      if (tokenExpiresAtMs - now <= 0) {
        clearInterval(id);
        logout();
      }
    }, 1000);
    return () => clearInterval(id);
  }, [tokenExpiresAtMs, logout]);

  return (
    <header className="app-header">
      <div className="header-content">
        {leftActions && <div className="header-left">{leftActions}</div>}
        <div className="user-info">
          <div className="user-details">
            <div className="user-name-row">
              <VehicleUrgentHeaderBadge />
              <span className="user-name">{user?.name}</span>
            </div>
            <span className="user-email">{user?.email}</span>
          </div>
          {remainLabel ? (
            <span className="token-remain" title="로그인 토큰 만료까지 남은 시간">
              {remainLabel}
            </span>
          ) : null}
          <button
            type="button"
            className="header-google-chat-btn"
            title="구글 챗 (새 탭)"
            aria-label="구글 챗 열기"
            onClick={openGoogleChat}
          >
            <GoogleChatIcon size={26} />
          </button>
          <PushBellButton />
          <button className="logout-button" onClick={logout}>
            로그아웃
          </button>
        </div>
      </div>
    </header>
  );
};

export default Header;

